<template>
    <div class="container-fluid content-wrapper" v-if='account'>
        <p-card>
            <b-row>
                <b-col cols="3" lg="1" class="ps-3 d-flex align-items-center">
                    <p-checkbox
                        inline
                        label="Active"
                        v-model="account.isActive"
                        useSwitch
                    />
                </b-col>
                <b-col cols="12" lg="3">
                    <p-input
                        label="Account Code"
                        rules="required|max:40"
                        v-model="account.accountCode"
                    />
                </b-col>
                <b-col cols="9" lg="3">
                    <p-input
                        label="Agency Name"
                        rules="required|max:80"
                        v-model="account.agencyName"
                    />
                </b-col>  
                <b-col cols="12" lg="5">
                    <p-feature-flag ref="BalanceDue" name="Balance Due">  
                    <p-number
                            label="Balance Due"
                            name="Balance Due"
                            v-model="account.accountBalance"
                            disabled="disabled"
                            input-type="money"
                            left-align
                        >
                            <template v-slot:append>
                                <b-button
                                    variant='outline-primary'
                                    @click="$bvModal.show('applyPaymentModal')"
                                    :disabled="isNew"
                                >Apply Payment</b-button>
                                <b-button
                                    variant='outline-primary'
                                    :disabled="isNew"
                                    @click="$bvModal.show('adjustBalanceModal')"
                                    v-if="$currentUserHasRole('Admin')"
                                >Adjust Balance</b-button>
                            </template>
                        </p-number>
                        </p-feature-flag>
                </b-col>              
            </b-row>
             <b-row cols="1" cols-lg="4">
                 <b-col>
                    <p-datepicker
                        label="Default Funding Start Date"
                        v-model="account.defaultStartDate"
                         @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                        vid="defaultStartDate"
                        rules='required'
                    />
                </b-col>
                <b-col>
                    <p-datepicker
                        label="Default Funding End Date"
                        v-model="account.defaultEndDate"
                        @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                        vid="defaultEndDate"
                        rules="required|dateGreaterOrEqual:@defaultStartDate"
                    />
                </b-col>
                <b-col>
                    <p-select
                        label="Fund Type"
                        :options="fundTypes"
                        rules="required"
                        v-model="account.accountType"
                        @change="onFundTypeChangeHandler()"
                    />
                </b-col>
                <b-col>
                    <p-select
                        label="Type"
                        :options="types"
                        text-field="text"
                        rules="required"
                        v-model="account.type"
                        @change="onTypeChangeHandler()"
                    />
                </b-col>
             </b-row>
             <b-row>
                 <b-col cols="12" lg="2">
                    <p-number
                        input-type="money"
                        label="Default Credit Limit"
                        left-align
                        :rules="getMinValue"
                        v-model="account.defaultCreditLimit"
                        :disabled="disableCreditLimit"
                    />
                </b-col>
                <b-col cols="12" lg="2" class="mt-3 pt-3" :class="account.noLimit ? 'text-red': ''">
                     <p-checkbox label="No Limit" :disabled="disabledNoLimit" v-model="account.noLimit" @change="onNoLimitChangeHandler()"/>
                </b-col>
                <b-col cols="12" lg="2">
                    <p-number
                        input-type="money"
                        label="Default Account Balance"
                        rules="min_value:0"
                        left-align
                        :disabled="disableAccountBalance"
                        v-model="account.defaultBalance"
                    />
                </b-col>
                <p-feature-flag name="Non Merch">
                    <b-col cols="6" lg="auto" class="mt-3 pt-3">
                        <p-checkbox label="Allow Non-Merch" v-model="account.isAllowNonMerch"/>
                    </b-col>
                </p-feature-flag>
                <!-- lg="auto" so the checkbox columns hug their content and Tax Exempt
                     sits next to Tax Exempt Code instead of across a fixed 2-col gap. -->
                <b-col cols="6" lg="auto" class="mt-3 pt-3">
                    <p-checkbox label="Tax Exempt" v-model="account.isTaxExempt"/>
                </b-col>
                <b-col cols="12" lg="2">
                    <p-input
                        label="Tax Exempt Code"
                        v-model="account.taxExemptId"
                        rules="max:15"
                    />
                </b-col>
             </b-row>
             <b-row cols="1" cols-lg="2">
                 <b-col>
                     <p-advanced-select
                        label="Tender"
                        data-type="tenders"
                        v-model="account.tenderId"
                        rules="selectRequired"
                    />
                </b-col>
                <b-col>
                    <p-input
                        label="Comments"
                        v-model="account.comment"
                        rules="max:256"
                    />
                </b-col>
             </b-row>
             <b-row cols="1" cols-lg="2">
                <b-col>
                    <p-address-input
                        label="Contact Address"
                        v-model="account.contactAddress"
                    />
                </b-col>
                <b-col>
                    <p-address-input
                        label="Billing Address"
                        v-model="account.billingAddress"
                        same-as-address-name="Contact"
                        :same-as-address="account.contactAddress"
                    />
                </b-col>
             </b-row>
             <b-row cols="1" cols-lg="2">
                 <b-col>
                     <p-phone-number
                        label="Contact Phone"
                        rules="max:26"
                        v-model="account.contactPhone.number"
                    />
                 </b-col>
                 <b-col>
                     <p-input
                        label="Contact Email"
                        rules="email|max:254"
                        v-model="account.contactEmail"
                    />
                 </b-col>
             </b-row>
        </p-card>
        <p-modal
            size="md"
            label="Apply Payment"
            name="applyPaymentModal"
            @ok.prevent="balanceDueUpdate(false)"
            @show="onModalShow('applyPaymentForm')"
            @hidden="onModalHidden()"
        >
                <template v-slot:default>
                    <p-form ref="applyPaymentForm">
                        <div>
                            <b-row>
                                <b-col>
                                    <strong>Account Code</strong>
                                </b-col>
                                <b-col>
                                    <strong>Agency Name</strong>
                                </b-col>
                                <b-col>
                                    <strong>Balance Due</strong>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>{{ account.accountCode }}</b-col>
                                <b-col>{{ account.agencyName }}</b-col>
                                <b-col>{{ currencyFormatter(account.accountBalance)}}</b-col>
                            </b-row>
                            <b-row class="mt-3">
                                <b-col>
                                    <p-input
                                        name="Payment Id"
                                        label="Payment Id"   
                                        model-value="Generated On Save"                                     
                                        disabled
                                    />
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <p-number
                                        name="Payment Amount"
                                        label="Payment Amount"
                                        v-model="balanceDueModalData.amount"
                                        input-type="money"
                                        rules="required|min_value:0"
                                    />
                                </b-col>
                                <b-col>
                                    <p-input
                                        name="Payment Reference"
                                        label="Payment Reference"
                                        v-model="balanceDueModalData.referenceNumber"
                                        rules="required|max:100"
                                    />
                                </b-col>
                            </b-row>
                            <p-feature-flag ref="AccountingFlag" name="Accounting">
                                <b-row>
                                    <b-col>
                                        <p-advanced-select
                                        label="GL Code"
                                        data-type="chartOfAccounts"
                                        v-model="payInChartOfAccountsId"
                                        :filter="filterchartofaccpayin"
                                        />
                                    </b-col>
                                </b-row>
                            </p-feature-flag>
                            <b-row>
                                <b-col>
                                    <p-text-area
                                        name="Payment Comment"
                                        label="Payment Comment"
                                        v-model="balanceDueModalData.comment"
                                        rules="max:4000"
                                    />
                                </b-col>
                            </b-row>
                        </div>
                    </p-form>
                    </template>
                    <template v-slot:modal-footer="{ ok, cancel }">
                        <b-row><b-col>
                        <p-button variant="outline-primary" @click="cancel" :is-busy="isBusy">Cancel</p-button>
                        <p-button variant="primary" @click="ok" class="ms-2" :is-busy="isBusy">Save</p-button>
                        </b-col></b-row>
                    </template>
        </p-modal>
        <p-modal
            size="md"
            label="Adjust Balance Due"
            name="adjustBalanceModal"
            @ok.prevent="balanceDueUpdate(true)"
            @show="onModalShow('adjustBalanceForm')"
            @hidden="onModalHidden()"
        >
                    <template v-slot:default>
                    <p-form ref="adjustBalanceForm">
                        <div>
                            <b-row>
                                <b-col>
                                    <strong>Account Code</strong>
                                </b-col>
                                <b-col>
                                    <strong>Agency Name</strong>
                                </b-col>
                                <b-col>
                                    <strong>Balance Due</strong>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>{{ account.accountCode }}</b-col>
                                <b-col>{{ account.agencyName }}</b-col>
                                <b-col>{{ currencyFormatter(account.accountBalance)}}</b-col>
                            </b-row>
                            <b-row class="mt-3">
                                <b-col>
                                    <p-input
                                        name="Adjustment Id"
                                        label="Adjustment Id"
                                        model-value="Generated On Save"
                                        disabled
                                    />
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <p-number
                                        name="New Balance"
                                        label="New Balance"
                                        v-model="balanceDueModalData.amount"
                                        input-type="money"
                                        rules="required|min_value:0"
                                    />
                                </b-col>                                
                            </b-row>
                            <p-feature-flag ref="AccountingFlag" name="Accounting">
                                <b-row>
                                    <b-col>
                                        <p-advanced-select
                                        label="GL Code"
                                        data-type="chartOfAccounts"
                                        v-model="miscChartOfAccountsId"
                                        :filter="filterchartofaccmisc"
                                        />
                                    </b-col>
                                </b-row>
                            </p-feature-flag>
                            <b-row>
                                <b-col>
                                    <p-text-area
                                        name="Adjustment Reason"
                                        label="Adjustment Reason"
                                        v-model="balanceDueModalData.comment"
                                        rules="required|max:4000"
                                    />
                                </b-col>
                            </b-row>
                        </div>
                    </p-form>
                    </template>
                    <template v-slot:modal-footer="{ ok, cancel }">
                        <b-row><b-col>
                        <p-button variant="outline-primary" @click="cancel" :is-busy="isBusy">Cancel</p-button>
                        <p-button variant="primary" @click="ok" class="ms-2" :is-busy="isBusy">Save</p-button>
                        </b-col></b-row>
                    </template>
                </p-modal>
    </div>
</template>
<script>

import axios from 'axios';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import {
    currencyFormatter
} from '@/components/Common/Formatters.js';
export default {
  components: {  },
    props: {
        account: Object,
        isNew:Boolean
    },
    computed: {
        disableCreditLimit() {
            return this.account.type === 2 || this.account.type === null || this.account.noLimit
        },
        disableAccountBalance() {
            return this.account.type === 1 || this.account.type === null;
        },
        getMinValue(){
            if(this.account.type === 2 || this.account.noLimit ===true){
                return 'min_value:0';
            }else{
                return 'required|min_value:0';
            }

        },
        disabledNoLimit(){
            return this.account.accountType===4 || this.account.type === 2;
        }
    },
    methods: {
        currencyFormatter: currencyFormatter,
        onTypeChangeHandler() {
            if (this.account.type === 2) {
                this.account.isDebit = true;
                this.account.defaultCreditLimit = null;
                this.account.noLimit=false
            } else {
                this.account.isDebit = false;
                this.account.defaultBalance = null;
            }
        },
        onFundTypeChangeHandler(){
            if(this.account.accountType === 4 ){
                this.account.noLimit=false
            }
        },
        onNoLimitChangeHandler(){
            if(this.account.noLimit){
                this.account.defaultCreditLimit = null;
            }
        },
        async balanceDueUpdate(isAdjustBalance){
            // Guard against a rapid double-click queuing two saves before the
            // post completes (the await on validate() leaves a window otherwise).
            if (this.isBusy) {
                return;
            }
            this.isBusy = true;

            const valid = isAdjustBalance
                ? await this.$refs.adjustBalanceForm.validate()
                : await this.$refs.applyPaymentForm.validate();
            if (!valid) {
                this.isBusy = false; // re-enable so the user can correct and retry
                return;
            }
            this.balanceDueModalData.isAdjustBalance = isAdjustBalance;
            this.balanceDueModalData.accountId = this.account.accountId;
            this.balanceDueModalData.accountBalance = this.account.accountBalance;
            this.balanceDueModalData.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            // Post the GL code from whichever modal is saving. Coerce to 0
            // (Unassigned) so the backend's non-nullable int never receives null.
            this.balanceDueModalData.chartOfAccountsId =
                Number(isAdjustBalance ? this.miscChartOfAccountsId : this.payInChartOfAccountsId) || 0;
            axios.post('accountReceivable/adjustbalancedue', this.balanceDueModalData).then(() => {
                if(isAdjustBalance){
                    this.$appSuccess(
                        `Balance due adjusted successfully.`
                    )
                }else{
                    this.$appSuccess(
                        `Account payment saved successfully.`
                    )
                }
                             
            }).finally(() => {
                this.isBusy = false;
                this.resetModal();
            });
        },
        filterchartofaccmisc(chartofaccount) {
            return chartofaccount.type == 'Miscellaneous' || chartofaccount.type == null;  
        },        
        filterchartofaccpayin(chartofaccount) {
            return chartofaccount.type == 'Pay In' || chartofaccount.type == null;  
        },
        resetModal() {
            // Save succeeded — close the modal and reload the account. The actual
            // input/validation clearing happens in onModalHidden, which fires for
            // every close (save, cancel, backdrop, Esc).
            this.$bvModal.hide('applyPaymentModal');
            this.$bvModal.hide('adjustBalanceModal');
            this.$emit('load-data');
        },
        onModalShow(refName) {
            // Reset validation on OPEN, not close. At open no field value is
            // changing, so nothing re-triggers validation after the reset and it
            // sticks. Resetting on close instead races the async re-validation
            // that clearing the required fields kicks off, which left stale red
            // errors showing on the next open.
            this.$nextTick(() => {
                this.$refs[refName]?.$refs?.observer?.resetForm?.();
            });
        },
        onModalHidden() {
            // Clear inputs whenever the modal closes (save, cancel, backdrop, Esc)
            // so reopening starts blank. Validation is cleared on open instead.
            this.balanceDueModalData.amount = null;
            this.balanceDueModalData.referenceNumber = null;
            this.balanceDueModalData.comment = null;
            this.balanceDueModalData.chartOfAccountsId = 0;
            this.payInChartOfAccountsId = 0;
            this.miscChartOfAccountsId = 0;
        }
    },
    data() {
        return {
            isBusy: false,
            fundTypes: [
                {value: 6, text: 'Revolving'},
                {value: 4, text: 'Expiring'},
                {value: 10, text: 'Department Charge'}
            ],
            types: [
                {value: 1, text: 'Credit'},
                {value: 2, text: 'Debit'}
            ],
            // Each modal needs its own GL Code model. Both p-advanced-selects are
            // mounted at once and each clears a value that fails its own filter
            // (Pay In vs Miscellaneous) — sharing one model let the Adjust Balance
            // select wipe the Apply Payment selection back to null. 0 = Unassigned.
            payInChartOfAccountsId: 0,
            miscChartOfAccountsId: 0,
            balanceDueModalData: {
                amount:null,
                referenceNumber:null,
                comment:null,
                chartOfAccountsId:0,
                locationId:null,
                accountId:null,
                accountBalance:null
            }
        }
    },
}
</script>
<style scoped>
.text-red {
    color:red;
}
</style>